/**
 * FamilyMemberCalendarResolver
 *
 * Helper class to determine which family members have calendars assigned.
 */

import type { CalendarMemberMapping } from '@/types/calendar-member-mapping';
import type { DeviceCalendar } from '@/lib/calendar/DeviceCalendarService';

export class FamilyMemberCalendarResolver {
  /**
   * Check if a member has any calendar assigned
   */
  static hasCalendar(memberId: string, mappings: CalendarMemberMapping[]): boolean {
    return mappings.some(mapping => mapping.memberIds.includes(memberId));
  }

  /**
   * Get the set of member IDs that have any linked calendar
   */
  static getMembersWithCalendars(mappings: CalendarMemberMapping[]): Set<string> {
    const memberIds = new Set<string>();
    for (const mapping of mappings) {
      for (const id of mapping.memberIds) {
        memberIds.add(id);
      }
    }
    return memberIds;
  }

  /**
   * Get the ownerAccount email for a member's linked calendar, or null
   */
  static getCalendarEmailForMember(
    memberId: string,
    mappings: CalendarMemberMapping[],
    calendars: DeviceCalendar[],
  ): string | null {
    const mapping = mappings.find(m => m.memberIds.includes(memberId));
    if (!mapping) return null;

    const calendar = calendars.find(c => c.id === mapping.calendarId);
    return calendar?.ownerAccount ?? null;
  }

  /**
   * Get all unique ownerAccount emails for a member (handles multiple calendar mappings)
   */
  static getAllCalendarEmailsForMember(
    memberId: string,
    mappings: CalendarMemberMapping[],
    calendars: DeviceCalendar[],
  ): string[] {
    const emails = new Set<string>();
    for (const mapping of mappings) {
      if (!mapping.memberIds.includes(memberId)) continue;
      const calendar = calendars.find(c => c.id === mapping.calendarId);
      if (calendar?.ownerAccount) {
        emails.add(calendar.ownerAccount);
      }
    }
    return Array.from(emails);
  }

  /**
   * Get a map of memberId → ownerAccount email for all members with calendars
   */
  static getCalendarEmailsForMembers(
    mappings: CalendarMemberMapping[],
    calendars: DeviceCalendar[],
  ): Map<string, string> {
    const result = new Map<string, string>();
    for (const mapping of mappings) {
      const calendar = calendars.find(c => c.id === mapping.calendarId);
      if (!calendar?.ownerAccount) continue;
      for (const memberId of mapping.memberIds) {
        result.set(memberId, calendar.ownerAccount);
      }
    }
    return result;
  }
}
